import React, { useState, useEffect, Fragment } from "react";
import api from "../../lib/clientApi";
import PageResultsTab from "./PageResultsTab";
import DocumentTab from "./DocumentTab";
import { ApiResult } from "../../lib/CmsApi";
import { Router } from "../../lib/routes";
import useDidUpdateEffect from "../../lib/useDidUpdateEffect";
import { Tabs, TabPanels, TabPanel } from "@reach/tabs";
import TabList from "./Tabs";
import SearchHeader from "./Header";
import CustomPageHead from "../common/CustomPageHead";
import { Image } from "../../lib/image";

export const SEARCH_TYPES = [
  { label: "Förderprogramme", id: "foerderprogramme" },
  { label: "Downloads", id: "downloads" },
  { label: "Weitere Ergebnisse", id: "weitere-ergebnisse" }
];

export const RESULT_PAGE_LIMIT = 15;
export const RESULT_DOCUMENT_LIMIT = 20;
const emptyResultSet = { items: [], total: 0 };

interface LoadProps {
  searchTerm: string;
  activeTab: string;
  page: number;
  checkedSegments?: string[];
}

const loadPrograms = ({
  activeTab,
  page,
  searchTerm,
  checkedSegments
}: LoadProps) =>
  api.listPrograms({
    limit: RESULT_PAGE_LIMIT,
    offset:
      (activeTab === SEARCH_TYPES[0].id ? page - 1 : 0) * RESULT_PAGE_LIMIT,
    search: { term: searchTerm as string },
    categories:
      checkedSegments && !!checkedSegments.length
        ? {
            eq: checkedSegments,
            path: "categoryCluster.segment"
          }
        : undefined
  });

const loadDocuments = ({ activeTab, page, searchTerm }: LoadProps) =>
  api.listContentBySearch(searchTerm as string, {
    includeModels: ["document"],
    limit: RESULT_DOCUMENT_LIMIT,
    linkableOnly: "false" as any, // TODO fix api
    offset:
      (activeTab === SEARCH_TYPES[1].id ? page - 1 : 0) * RESULT_DOCUMENT_LIMIT
  });

const loadOthers = ({ activeTab, page, searchTerm }: LoadProps) =>
  api.listContentBySearch(searchTerm as string, {
    excludeModels: ["program", "document"],
    linkableOnly: true,
    limit: RESULT_PAGE_LIMIT,
    offset:
      (activeTab === SEARCH_TYPES[2].id ? page - 1 : 0) * RESULT_PAGE_LIMIT
  });

const parseProgramResults = ({ total, items }: ApiResult<"listPrograms">) => ({
  total,
  items: items.map(p => ({
    id: p._id,
    title: p.name,
    description: p.abstract,
    url: `/foerderprogramm/${p.slug}`
  }))
});

const getTotals = (
  programResults = emptyResultSet,
  documentResults = emptyResultSet,
  otherResults = emptyResultSet
) => ({
  [SEARCH_TYPES[0].id]: programResults.total,
  [SEARCH_TYPES[1].id]: documentResults.total,
  [SEARCH_TYPES[2].id]: otherResults.total
});

export interface SearchItem {
  id: string;
  title: string;
  description?: string;
  image?: Image;
  url?: string;
}
export interface SearchResults {
  total: number;
  items: SearchItem[];
}

export interface Props {
  activeTab: string;
  searchTerm: string;
  checkedSegments?: string[];
  page: number;
}

export default function SearchPage(props: Props) {
  const { activeTab: tab, page, checkedSegments } = props;

  const [activeTab, setActiveTab] = useState(tab);
  const [programResults, setProgramResults] = useState(emptyResultSet);
  const [documentResults, setDocumentResults] = useState(emptyResultSet);
  const [otherResults, setOtherResults] = useState(emptyResultSet);
  const [searchTerm, setSearchTerm] = useState(props.searchTerm);
  const [tabChangedManually, setTabChangedManually] = useState(false);

  const totals = getTotals(programResults, documentResults, otherResults);

  useEffect(() => {
    let isCanceled = false;

    async function search() {
      const defaultProps = { searchTerm, activeTab, page };
      const [programResults, documentResults, otherResults] = await Promise.all(
        [
          loadPrograms({ ...defaultProps, checkedSegments }),
          loadDocuments(defaultProps),
          loadOthers(defaultProps)
        ]
      );

      if (isCanceled) return;

      setProgramResults(parseProgramResults(programResults));
      setDocumentResults(documentResults);
      setOtherResults(otherResults);
      setTabChangedManually(false);
    }

    // clear search when no search term is given
    if (!searchTerm) {
      setOtherResults(emptyResultSet);
      setProgramResults(emptyResultSet);
      setDocumentResults(emptyResultSet);
      return;
    }
    search();
    return () => {
      isCanceled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...(checkedSegments || [false]), page, searchTerm]);

  // select tab with results
  useEffect(() => {
    if (tabChangedManually) return;
    const totals = getTotals(programResults, documentResults, otherResults);
    if (!totals[activeTab]) {
      const fullTabs = Object.entries(totals)
        .filter(([, total]) => !!total)
        .map(([key]) => key);
      if (fullTabs.length > 0 && !fullTabs.includes(activeTab))
        setActiveTab(fullTabs[0]);
    }
  }, [
    activeTab,
    documentResults,
    otherResults,
    programResults,
    tabChangedManually
  ]);

  // update search term when term in url changes (search input in nav)
  useDidUpdateEffect(() => {
    if (props.searchTerm !== searchTerm) setSearchTerm(props.searchTerm);
  }, [props.searchTerm]);

  // load data according to page in pagination
  useDidUpdateEffect(() => {
    let isCanceled = false;
    if (!searchTerm) return;
    async function search() {
      const defaultProps = { searchTerm, activeTab, page };
      let results: ApiResult<"listContentBySearch"> | ApiResult<"listPrograms">;

      switch (activeTab) {
        case SEARCH_TYPES[0].id:
          results = parseProgramResults(
            await loadPrograms({ ...defaultProps, checkedSegments })
          );
          break;
        case SEARCH_TYPES[1].id:
          results = await loadDocuments(defaultProps);
          break;
        case SEARCH_TYPES[2].id:
          results = await loadOthers(defaultProps);
          break;
      }

      if (isCanceled) return;

      switch (activeTab) {
        case SEARCH_TYPES[0].id:
          setProgramResults(results);
          break;
        case SEARCH_TYPES[1].id:
          setDocumentResults(results);
          break;
        case SEARCH_TYPES[2].id:
          setOtherResults(results);
          break;
      }
    }

    search();
    return () => {
      isCanceled = true;
    };
  }, [page]);

  // update program search results, when selected segments change
  useDidUpdateEffect(() => {
    let isCanceled = false;
    if (!searchTerm) return;

    async function search() {
      const programResults = await loadPrograms({
        searchTerm,
        activeTab,
        page,
        checkedSegments
      });
      if (isCanceled) return;
      setProgramResults(parseProgramResults(programResults));
    }

    search();
    return () => {
      isCanceled = true;
    };
  }, [...(checkedSegments || [false])]);

  // update active tab in url
  useDidUpdateEffect(() => {
    function setNewActiveTab() {
      const { tab, page, ...query } = Router.query;
      if (tab === activeTab) return;
      Router.replaceRoute(Router.route.replace("/", ""), {
        ...query,
        tab: activeTab,
        page: 1
      });
    }
    setNewActiveTab();
  }, [activeTab]);

  return (
    <Fragment>
      <CustomPageHead title="Suche" />
      <SearchHeader
        activeTab={activeTab}
        searchTerm={searchTerm}
        checkedSegments={checkedSegments}
      />
      <Tabs
        as={"main"}
        onChange={index => {
          setActiveTab(SEARCH_TYPES[index].id);
          setTabChangedManually(true);
        }}
        index={SEARCH_TYPES.findIndex(t => t.id === activeTab)}
      >
        <TabList
          items={SEARCH_TYPES.map(t => ({
            label: t.label,
            count: totals[t.id]
          }))}
        />
        <TabPanels>
          <TabPanel>
            <PageResultsTab
              results={programResults}
              page={page}
              name={SEARCH_TYPES[0].label}
            />
          </TabPanel>
          <TabPanel>
            <DocumentTab
              results={documentResults}
              page={page}
              name={SEARCH_TYPES[1].label}
            />
          </TabPanel>
          <TabPanel>
            <PageResultsTab
              results={otherResults}
              page={page}
              name={SEARCH_TYPES[2].label}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Fragment>
  );
}
